import React, { useEffect, useMemo, useRef, useState } from 'react';
import { upcomingDeliveryDays, toDateKey } from '../utils/deliveryDays';
import { CalendarIcon } from './icons/LucideIcons';
import { useAppearance } from '../contexts/AppearanceContext';
import DateCalendarPopover from './DateCalendarPopover';

/**
 * Delivery-date field: a row of tappable days plus an escape hatch.
 *
 * Most orders go out today or tomorrow, and a date input costs three
 * interactions to say so. The row makes the common case one tap, and "Other
 * date" keeps every other date reachable.
 *
 * Days the shop is closed and blacked-out dates are omitted from the ROW, since
 * offering them would suggest a date the order cannot be fulfilled on. They stay
 * selectable through the picker — staff sometimes know something the settings
 * do not, and this field should not be the thing that argues with them.
 */
const isTomorrow = (date) => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return date.toDateString() === t.toDateString();
};

const DeliveryDatePicker = ({
  value,
  onChange,
  storeHours,
  blackoutDates = [],
  count = 6,
  label = 'Delivery Date',
  required = false,
  id = 'delivery-date',
  allowPast = false, // house accounts can be billed for past service
  from = undefined, // first day to offer (the earliest the rules allow); default today
  capacity = null, // rules.capacity — full days are shown, labelled, never hidden
  // 'tiles': the Forest New order's 52×52 day tiles and dashed "Other".
  variant,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const dateInputRef = useRef(null);
  const calendarOpenerRef = useRef(null);
  // Fable follows the Bouqmark order-entry design: three tall date cards and a
  // "Pick date" card, four across. Rosa keeps the compact row of day chips.
  const { appearance } = useAppearance();
  const isFable = appearance === 'fable';
  const rowCount = isFable ? 3 : count;

  const days = useMemo(
    () => upcomingDeliveryDays({ ...(from ? { from } : {}), storeHours, blackoutDates, capacity, count: rowCount }),
    [from, storeHours, blackoutDates, capacity, rowCount]
  );

  // A value outside the row — a further-out date, or one the row skips — has to
  // stay visible, or the field would look empty for a date that is genuinely set.
  const valueInRow = days.some((d) => d.key === value);
  const showCustom = !!value && !valueInRow;

  useEffect(() => {
    // Fable opens its own calendar (below); only Rosa hands off to the browser's.
    if (showPicker && !isFable && dateInputRef.current) {
      dateInputRef.current.focus();
      // showPicker() throws NotAllowedError without a fresh user gesture (and in
      // some embedded contexts). Uncaught here it would take down the whole
      // order page; the focused input is a fine fallback.
      try {
        dateInputRef.current.showPicker?.();
      } catch {
        // keep the focused input
      }
    }
  }, [showPicker, isFable]);

  const custom = showCustom ? new Date(`${value}T00:00:00`) : null;

  // `relative` is load-bearing: the native date input below is `sr-only`
  // (position: absolute) when unused. Without a positioned ancestor it measures
  // from the page itself, escapes the app's scroll container, and stretches the
  // document to its static position — a second scrollbar with up to a page of
  // empty space under the form (worst in Fable, where this field sits last).
  return (
    <div className="relative">
      {(label || variant !== 'tiles') && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
          {label} {required && <span className="text-floral-700">*</span>}
        </label>
      )}

      {isFable ? (
        <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {days.map((d) => {
            const selected = value === d.key;
            const kicker =
              d.relativeLabel ||
              (isTomorrow(d.date) ? 'Tomorrow' : d.date.toLocaleDateString('en-US', { weekday: 'long' }));
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => onChange(d.key)}
                aria-pressed={selected}
                className={`date-card flex flex-col items-start gap-0.5 p-3.5 rounded-3xl border-2 text-left transition-colors ${
                  selected
                    ? 'bg-floral-200 border-floral-700 text-floral-900'
                    : 'bg-gray-100 border-gray-300 text-gray-900 hover:border-floral-500'
                }`}
              >
                <span className={`text-[11px] font-bold uppercase tracking-[0.08em] ${selected ? 'opacity-75' : 'text-gray-600'}`}>
                  {kicker}
                </span>
                <span className="font-heading text-[17px] leading-tight">
                  {d.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
                <span className={`text-xs font-semibold ${d.full ? 'text-red-700' : selected ? 'opacity-80' : 'text-gray-600'}`}>
                  {d.full ? 'Full' : d.remaining !== null && d.remaining <= 3 ? `${d.remaining} left` : 'Open'}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            ref={calendarOpenerRef}
            data-calendar-opener
            onClick={() => setShowPicker((v) => !v)}
            aria-pressed={showCustom}
            aria-expanded={showPicker}
            aria-haspopup="dialog"
            className={`date-card flex flex-col items-start gap-0.5 p-3.5 rounded-3xl border-2 text-left transition-colors ${
              showCustom
                ? 'bg-floral-200 border-floral-700 text-floral-900'
                : 'bg-transparent border-dashed border-gray-400 text-gray-800 hover:border-floral-500'
            }`}
          >
            <span className={`text-[11px] font-bold uppercase tracking-[0.08em] ${showCustom ? 'opacity-75' : 'text-gray-600'}`}>
              Later
            </span>
            <span className="font-heading text-[17px] leading-tight flex items-center gap-1.5">
              {showCustom ? (
                custom.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
              ) : (
                <>
                  <CalendarIcon size={17} />
                  Pick date
                </>
              )}
            </span>
            <span className={`text-xs font-semibold ${showCustom ? 'opacity-80' : 'text-gray-600'}`}>
              {showCustom ? 'Change date' : 'Opens calendar'}
            </span>
          </button>
          {showPicker && (
            <DateCalendarPopover
              value={value}
              onChange={onChange}
              onClose={({ returnFocus } = {}) => {
                setShowPicker(false);
                if (returnFocus) calendarOpenerRef.current?.focus();
              }}
              storeHours={storeHours}
              blackoutDates={blackoutDates}
              allowPast={allowPast}
            />
          )}
        </div>
      ) : variant === 'tiles' ? (
      <div className="flex flex-wrap gap-1.5">
        {days.map((d) => {
          const selected = value === d.key;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => onChange(d.key)}
              aria-pressed={selected}
              aria-label={`${d.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${d.full ? ', full' : ''}`}
              className={`flex h-[52px] w-[52px] flex-col items-center justify-center rounded-md border leading-tight transition-colors duration-[120ms] ${
                selected
                  ? 'border-floral-700 bg-floral-700 text-white'
                  : 'border-gray-300 bg-white text-gray-900 hover:border-floral-700'
              }`}
            >
              <span className="text-[10px] font-semibold uppercase tracking-[0.06em] opacity-80">
                {d.relativeLabel || d.dayLabel}
              </span>
              <span className="text-lg font-semibold tabular-nums">{d.dayNumber}</span>
              {d.full && (
                <span className={`text-[9px] font-semibold uppercase leading-none ${selected ? 'text-white/80' : 'text-danger-fg'}`}>Full</span>
              )}
            </button>
          );
        })}

        {/* Anything the row does not cover; a far-out date shows here as the selection. */}
        <button
          type="button"
          onClick={() => setShowPicker((v) => !v)}
          aria-pressed={showCustom}
          className={`flex h-[52px] w-[52px] flex-col items-center justify-center rounded-md border leading-tight transition-colors duration-[120ms] ${
            showCustom
              ? 'border-floral-700 bg-floral-700 text-white'
              : 'border-dashed border-gray-300 bg-transparent text-gray-600 hover:border-floral-700 hover:text-floral-700'
          }`}
          title="Pick another date"
        >
          {showCustom ? (
            <>
              <span className="text-[10px] font-semibold uppercase tracking-[0.06em] opacity-80">
                {custom.toLocaleDateString('en-US', { month: 'short' })}
              </span>
              <span className="text-lg font-semibold tabular-nums">{custom.getDate()}</span>
            </>
          ) : (
            <span className="text-[11px] font-medium">Other</span>
          )}
        </button>
      </div>
      ) : (
      <div className="flex flex-wrap items-stretch gap-1.5">
        {days.map((d) => {
          const selected = value === d.key;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => onChange(d.key)}
              aria-pressed={selected}
              className={`flex flex-col items-center justify-center min-w-[3.25rem] px-2.5 py-1.5 rounded-lg border transition-colors ${
                selected
                  ? 'bg-floral-700 border-floral-700 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-floral-500'
              }`}
            >
              <span
                className={`text-[10px] font-semibold uppercase tracking-wide leading-none ${
                  selected ? 'text-white/80' : 'text-gray-500'
                }`}
              >
                {d.relativeLabel || d.dayLabel}
              </span>
              <span className="text-lg font-semibold leading-tight tabular-nums">{d.dayNumber}</span>
              {d.full && (
                <span className={`text-[9px] font-semibold uppercase leading-none ${selected ? 'text-white/80' : 'text-red-700'}`}>Full</span>
              )}
            </button>
          );
        })}

        {/* Anything the row does not cover. Rendered as a peer of the days so a
            far-out date reads as a selection rather than an empty field. */}
        <button
          type="button"
          onClick={() => setShowPicker((v) => !v)}
          aria-pressed={showCustom}
          className={`flex flex-col items-center justify-center min-w-[3.25rem] px-2.5 py-1.5 rounded-lg border transition-colors ${
            showCustom
              ? 'bg-floral-700 border-floral-700 text-white'
              : 'bg-white border-gray-300 text-gray-600 hover:border-floral-500'
          }`}
          title="Pick another date"
        >
          {showCustom ? (
            <>
              <span className="text-[10px] font-semibold uppercase tracking-wide leading-none text-white/80">
                {custom.toLocaleDateString('en-US', { month: 'short' })}
              </span>
              <span className="text-lg font-semibold leading-tight tabular-nums">
                {custom.getDate()}
              </span>
            </>
          ) : (
            <>
              <CalendarIcon size={16} strokeWidth={2.25} />
              <span className="text-[10px] font-medium leading-none mt-0.5">Other</span>
            </>
          )}
        </button>
      </div>
      )}

      {/* The native input stays mounted but visually collapsed when unused, so
          the value is always part of the form and validation still sees it. */}
      <input
        ref={dateInputRef}
        id={id}
        type="date"
        required={required}
        value={value || ''}
        min={toDateKey(new Date())}
        onChange={(e) => {
          onChange(e.target.value);
          setShowPicker(false);
        }}
        className={`mt-2 block w-full sm:w-56 ${
          variant === 'tiles' ? 'input-field sm:text-base' : 'rounded-lg border-gray-300 text-sm'
        } ${
          !isFable && (showPicker || showCustom) ? '' : 'sr-only'
        }`}
      />
    </div>
  );
};

export default DeliveryDatePicker;
